import { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import NotificationContainer from "./NotificationContainer";
import NotificationSkeleton from "@Components/Skeletons/NotificationSkeleton";
import utilService from "@Utils/utils.service";

interface NotificationsProps {
  children?: React.ReactNode;
}

const Notifications = ({ children }: NotificationsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    notificationItems,
    handleMarkAllRead,
    handleNotificationClick,
    readNotifications,
    isNotificationListLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = NotificationContainer({ isOpen, onClose: () => setIsOpen(false) });

  const toggleNotifications = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative inline-flex items-center">
      <div onClick={toggleNotifications}>{children}</div>

      {isOpen && (
        <div className="absolute right-0 top-[calc(100%+8px)] w-[400px] max-h-[500px] !overflow-y-auto custom-scrollbar bg-white rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.12)] border border-gray-100 z-[1000] flex flex-col overflow-hidden  animate-slideDown">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-2">
            <h3 className="!text-[16px] font-semibold text-gray-800">
              Notifications
            </h3>
            <button
              onClick={handleMarkAllRead}
              className="!text-[14px] font-semibold text-red-500 underline cursor-pointer"
            >
              Mark All as Read
            </button>
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-100" />
       
          <div
            className="flex-1 overflow-y-auto custom-scrollbar"
            onScroll={(event) =>
              utilService.handleFetchOnScroll(event, {
                hasNextPage,
                isFetchingNextPage,
                fetchNextPage,
              } as any)
            }
          >
            {isNotificationListLoading ? (
              <>
                {[...Array(5)].map((_, index) => (
                  <NotificationSkeleton key={index} />
                ))}
              </>
            ) : Array.isArray(notificationItems) && notificationItems.length > 0 ? (
              notificationItems.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={clsx(
                    "flex gap-3 cursor-pointer border-b border-gray-100 transition-colors",

                    notification.status === "Read" ||
                      readNotifications.has(notification.id)
                      ? "bg-transparent"
                      : "bg-red-50",
                  )}
                >
                  {/* Left Color Bar */}

                  {notification.status !== "Read" &&
                  !readNotifications.has(notification.id) ? (
                    <div className="w-[6px] bg-red-500" />
                  ) : (
                    <div className="w-1 " />
                  )}

                  {/* Content */}
                  <div className="py-2 flex flex-col">
                    <h4 className="!text-[14px] !font-semibold leading-5 text-gray-800 !py-0">
                      {notification.title}
                    </h4>

                    <p className="text-[13px] text-gray-500 !leading-5 !py-0">
                      {notification.description}
                    </p>

                    <p className="!text-[13px] text-gray-400  leading-4 !py-1">
                      {notification.timestamp}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center py-8 px-5">
                <p className="text-gray-400 text-[14px]">
                  No notifications yet
                </p>
              </div>
            )}
            {isFetchingNextPage && (
              <>
                {[...Array(2)].map((_, index) => (
                  <NotificationSkeleton key={`notification-next-${index}`} />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
